'use client';

import { useState, useEffect, useRef, Suspense } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import { PasswordInput } from "@workspace/ui/components/password-input";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/ui/components/accordion";
import { apiClient } from "@/lib/api-client";
import { startOAuth } from "@/lib/oauth";
import { getErrorMessage } from "@/lib/api-error";
import { storeAuthTokens } from "@/lib/auth-utils";
import { getDeviceInfo } from "@/lib/utils";
import { computeFingerprint } from "@/lib/fingerprint";
import { useFormPrefill, createFormLink } from "@/hooks/useFormPrefill";
import { useReturnTo } from "@/hooks/useReturnTo";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { UserAvatar } from "@workspace/ui/components/avatar";
import { OAUTH_PROVIDERS_METADATA, getOAuthProviderMetadata, type OAuthProvider } from "@workspace/auth-shared/types";
import { ENABLED_METHODS } from "@/lib/enabled-methods.generated";
import { startAuthentication } from "@simplewebauthn/browser";

const loginSchema = z.object({
  email: z.string().email("Invalid email address").max(320),
  password: z.string().min(1, "Password is required").max(128),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginOptions {
  methods: string[];
  recommended: string | null;
  mostUsed: string | null;
  profile: { avatarUrl?: string; initials?: string; avatarBgColor?: string } | null;
}

/** Human label for a method id (e.g. "oauth:google" → "Google"). */
function methodLabel(method: string): string {
  if (method.startsWith("oauth:")) {
    const id = method.slice(6);
    return getOAuthProviderMetadata(id as OAuthProvider)?.name ?? id;
  }
  switch (method) {
    case "password":
      return "your password";
    case "magic-link":
      return "a secure email link";
    case "passkey":
      return "your passkey";
    case "phone":
      return "your phone";
    default:
      return method;
  }
}

/**
 * Label for a "Sign in with {…}" button (noun form, no leading article).
 * e.g. "Sign in with " + "Passkey" / "Google" / "a secure email link".
 */
function methodActionLabel(method: string): string {
  if (method.startsWith("oauth:")) {
    const id = method.slice(6);
    return getOAuthProviderMetadata(id as OAuthProvider)?.name ?? id;
  }
  switch (method) {
    case "password":
      return "your password";
    case "magic-link":
      return "a secure email link";
    case "passkey":
      return "Passkey";
    case "phone":
      return "your phone";
    default:
      return method;
  }
}

function LoginContent() {
  const router = useRouter();
  const { returnTo, redirectAfterAuth, buildReturnToParam, appendReturnTo } = useReturnTo();

  // Already signed in + a `return_to` → ReturnToGuard (root layout) sends them
  // on. This only reports the session so the notice below can render.
  const { user, authenticated, loading: authLoading } = useAuth();

  const [error, setError] = useState<string | null>(null);
  /**
   * Set when login is rejected with FORBIDDEN / "Account is not active".
   * The account exists and the password was right, so a plain error string is a
   * dead end: the only way forward is a fresh verification email. Holding the
   * address lets the message carry that action (tdd-0093).
   */
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [isLoading, setIsLoading] = useState(false);
  const [showBskyHandle, setShowBskyHandle] = useState(false);
  const [bskyHandle, setBskyHandle] = useState("");
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [loginOptions, setLoginOptions] = useState<LoginOptions | null>(null);
  const lookupSeq = useRef(0);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  // Prefill email from URL query params
  useFormPrefill(form, ["email"]);

  // Watch email field for creating dynamic links
  const emailValue = form.watch("email");

  // Account-aware login: when the email looks valid (e.g. the browser autofilled
  // it) and a device fingerprint is available, ask the backend which methods
  // this account uses so we can surface the most likely one. Gated server-side
  // behind a "device has logged in before" soft signal — unknown devices and
  // unknown emails get a uniform default, so this leaks nothing. Degrades
  // silently on any error: the plain form is always the fallback.
  useEffect(() => {
    const email = emailValue?.trim();
    const validEmail = !!email && z.string().email().safeParse(email).success;
    const fingerprint = validEmail ? computeFingerprint() : undefined;

    // Bump the sequence on every change so any in-flight request is superseded
    // — otherwise a slow response could repopulate the suggestion (and avatar)
    // after the email was cleared or invalidated.
    const seq = ++lookupSeq.current;

    if (!validEmail || !fingerprint) {
      // Invalid email, or no stable device signal → enhancement stays off
      // (transparent opt-out). Clear any prior suggestion.
      setLoginOptions(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.v1.getLoginOptions({ email, fingerprint });
        if (seq !== lookupSeq.current) return; // a newer keystroke superseded this
        setLoginOptions(data as LoginOptions);
      } catch {
        if (seq === lookupSeq.current) setLoginOptions(null);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [emailValue]);

  // The single method most worth surfacing: a recommended==mostUsed match is a
  // strong default; otherwise fall back to whichever is known.
  const suggestedMethod =
    loginOptions &&
    loginOptions.recommended &&
    loginOptions.recommended === loginOptions.mostUsed
      ? loginOptions.recommended
      : loginOptions?.recommended ?? loginOptions?.mostUsed ?? null;

  // The recent method, if any, leads the layout. Password is special: rather
  // than a "sign in with your password" button that just reveals the form, the
  // email+password form itself becomes the primary content. Any other method
  // (passkey/oauth/magic-link/phone) gets a single "Sign in with {…}" button up
  // top and pushes the form + everything else into the "Other options" drawer.
  const primaryMethod = suggestedMethod;
  const formIsPrimary = !primaryMethod || primaryMethod === "password";

  const handleOAuthLogin = (provider: OAuthProvider) => {
    if (provider === "atproto") {
      setShowBskyHandle(true);
      return;
    }
    setIsOAuthLoading(true);
    setError(null);
    startOAuth(provider, { returnTo });
  };

  // Passkey sign-in, run inline so the recent-method button can trigger the
  // platform authenticator without a page hop. Uses discoverable credentials
  // (no username); falls back to the dedicated page's username/email selectors
  // if the user has multiple accounts.
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true);
    setError(null);
    try {
      if (!window.PublicKeyCredential) {
        setError("Passkeys are not supported in this browser");
        return;
      }

      const beginResponse = await apiClient.v1.beginWebAuthnLogin({});
      const { challengeId, options } = beginResponse.data;

      let credential;
      try {
        credential = await startAuthentication({ optionsJSON: options as any });
      } catch (webauthnError: any) {
        if (webauthnError.name === "NotAllowedError") {
          throw new Error("Authentication was cancelled or timed out");
        }
        throw new Error(`Passkey authentication failed: ${webauthnError.message}`);
      }

      const completeResponse = await apiClient.v1.completeWebAuthnLogin({
        challengeId,
        credential: credential as any,
        deviceInfo: getDeviceInfo(),
      });

      if (completeResponse.data.accessToken) {
        storeAuthTokens(
          completeResponse.data.accessToken,
          completeResponse.data.refreshToken,
        );
      }
      redirectAfterAuth();
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        setError("Authentication was cancelled or timed out");
      } else if (err.error?.code === "NOT_FOUND") {
        setError("Passkey not found. Try another passkey, or sign in another way and add a passkey in your account settings.");
      } else if (err.error?.code === "WEBAUTHN_ERROR") {
        setError(err.error.message || "Passkey authentication failed");
      } else if (err.error?.code === "INVALID_CREDENTIALS") {
        setError(err.error.message || "Invalid credentials");
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleBskyLogin = async () => {
    if (!bskyHandle.trim()) return;
    setIsOAuthLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.v1.atprotoLogin({
        handle: bskyHandle.trim(),
        returnTo: returnTo ?? undefined,
      });
      if (data.authUrl) window.location.href = data.authUrl;
    } catch {
      setError("Failed to connect to Bluesky. Check the handle and try again.");
      setIsOAuthLoading(false);
    }
  };

  // Act on a method: passkey + OAuth kick off their flow inline (no page hop),
  // magic-link/phone route to their dedicated multi-step page (email prefilled).
  // "password" needs no action — the password field lives on this page.
  const runMethod = (method: string) => {
    if (method.startsWith("oauth:")) {
      handleOAuthLogin(method.slice(6) as OAuthProvider);
      return;
    }
    if (method === "passkey") {
      handlePasskeyLogin();
      return;
    }
    const emailQuery = emailValue ? `?email=${encodeURIComponent(emailValue)}` : "";
    const routes: Record<string, string> = {
      "magic-link": "/login/magic-link",
      phone: "/login/phone",
    };
    const path = routes[method];
    if (path) router.push(appendReturnTo(`${path}${emailQuery}`));
  };

  /** Send a fresh verification email for an account that exists but is inactive. */
  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendState("sending");
    try {
      await apiClient.v1.resendVerification({ email: unverifiedEmail });
      setResendState("sent");
    } catch {
      // Deliberately not surfacing the API error: this endpoint returns 200 for
      // unknown addresses by design (no account enumeration), so a failure here
      // is a transport problem, not something the user can act on differently.
      setResendState("failed");
    }
  };

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.v1.loginWithEmail({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        deviceInfo: getDeviceInfo(),
      });

      // Check if MFA is required (API may return MFARequiredResponse instead of AuthResponse)
      const responseData = response.data as any;
      if (responseData.challengeToken) {
        const challengeToken = responseData.challengeToken;
        const mfaUrl = `/mfa/challenge?token=${encodeURIComponent(challengeToken)}${buildReturnToParam()}`;
        router.push(mfaUrl);
        return;
      }

      // Store tokens if returned in body (legacy), otherwise cookies handle auth
      if (response.data.accessToken) {
        storeAuthTokens(response.data.accessToken, response.data.refreshToken);
      }

      // Redirect after successful login
      redirectAfterAuth();
    } catch (err: any) {
      // Check if MFA is required (202 status code with challengeToken)
      if (err.status === 202) {
        // The response data might be in different places depending on API client
        const challengeToken =
          err.data?.challengeToken ||
          err.error?.challengeToken ||
          err.challengeToken;

        if (challengeToken) {
          const mfaUrl = `/mfa/challenge?token=${encodeURIComponent(challengeToken)}${buildReturnToParam()}`;
          router.push(mfaUrl);
          return;
        }
      }

      if (err.error?.code === "INVALID_CREDENTIALS") {
        setError("Invalid email or password");
        } else if (
          err.error?.code === "FORBIDDEN" &&
          /not active/i.test(err.error?.message ?? "")
        ) {
          // The account exists and the password was correct: it is simply
          // unverified. A plain error string is a dead end here, so surface the
          // one action that unblocks them instead (tdd-0093).
          setUnverifiedEmail(data.email);
          setError(null);
      } else if (err.error?.code === "ACCOUNT_LOCKED") {
        setError(err.error.message || "Account is temporarily locked. Please try again later.");
      } else if (err.error?.code === "RATE_LIMITED") {
        setError(
          err.error.message || `Too many attempts. Please try again in ${err.error.retryAfter} seconds.`
        );
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // The email + password form. Primary content when there's no recent method
  // or the recent method is password; otherwise it lives in the drawer.
  const formBlock = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        data-test-id="login-form"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="name@example.com"
                  autoComplete="email"
                  data-test-id="email-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-test-id="password-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between">
          <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    data-test-id="remember-me-checkbox"
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  Remember me
                </FormLabel>
              </FormItem>
            )}
          />

          <Link
            href={createFormLink("/password/forgot", {
              email: emailValue,
            })}
            className="text-sm underline hover:text-primary"
            data-test-id="forgot-password-link"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          data-test-id="login-submit"
        >
          {isLoading ? "Signing in..." : "Sign in"}
        </Button>
      </form>
    </Form>
  );

  // Alternate passwordless methods (passkey / magic link / phone), minus the one
  // already surfaced as the primary action. Each kicks off its flow via runMethod.
  const alternateMethods = (
    [
      ENABLED_METHODS.passkey && { id: "passkey", label: "Sign in with passkey" },
      ENABLED_METHODS.magicLink && { id: "magic-link", label: "Email me a sign-in link" },
      ENABLED_METHODS.phone && { id: "phone", label: "Sign in with your phone" },
    ].filter(Boolean) as { id: string; label: string }[]
  ).filter((m) => m.id !== primaryMethod);

  const alternateMethodsBlock = alternateMethods.length > 0 && (
    <div className="grid gap-2">
      {alternateMethods.map((m) => (
        <Button
          key={m.id}
          type="button"
          variant="outline"
          className="w-full"
          disabled={isOAuthLoading || isPasskeyLoading}
          onClick={() => runMethod(m.id)}
          data-test-id={`alt-method-${m.id}`}
        >
          {m.id === "passkey" && isPasskeyLoading ? "Authenticating..." : m.label}
        </Button>
      ))}
    </div>
  );

  const oauthBlock = ENABLED_METHODS.oauthProviders.length > 0 && (
    <>
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {OAUTH_PROVIDERS_METADATA.filter(
          (p) =>
            p.id !== "atproto" &&
            ENABLED_METHODS.oauthProviders.includes(p.id),
        ).map((provider) => (
          <Button
            key={provider.id}
            variant="outline"
            size="sm"
            onClick={() => handleOAuthLogin(provider.id)}
            disabled={isOAuthLoading}
          >
            {provider.name}
          </Button>
        ))}
        {ENABLED_METHODS.oauthProviders.includes("atproto") && (
          <Button
            variant="outline"
            size="sm"
            className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-800"
            onClick={() => handleOAuthLogin("atproto")}
            disabled={isOAuthLoading}
          >
            Bluesky
          </Button>
        )}
      </div>

      {showBskyHandle && (
        <div className="mt-3 space-y-2 rounded-md border p-3">
          <label className="text-sm font-medium" htmlFor="bsky-handle">
            Enter your Bluesky handle
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
              <Input
                id="bsky-handle"
                className="pl-7"
                placeholder="user.bsky.social"
                value={bskyHandle}
                onChange={(e) => setBskyHandle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBskyLogin(); }}
                disabled={isOAuthLoading}
              />
            </div>
            <Button
              onClick={handleBskyLogin}
              disabled={isOAuthLoading || !bskyHandle.trim()}
              size="sm"
            >
              {isOAuthLoading ? "..." : "Go"}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  // Everything not surfaced as the primary action, tucked into the drawer. When
  // the form is primary we still want passwordless + OAuth alternates here; when
  // a passwordless method is primary, the form joins them in the drawer.
  const otherOptionsBlock = (
    <div className="space-y-4">
      {!formIsPrimary && formBlock}
      {alternateMethodsBlock}
      {oauthBlock}
    </div>
  );

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      data-test-id="login-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            {formIsPrimary
              ? "Enter your email and password to sign in"
              : "Pick up where you left off"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/*
            Already signed in. With a `return_to`, ReturnToGuard is normally
            redirecting while this shows; it stays when the guard declines
            (the target bounced them straight back). Without one we genuinely
            cannot infer intent — they might want their profile, might have
            forgotten they were signed in, or might be here to switch accounts
            — so say the state plainly and leave all three doors open.
          */}
          {!authLoading && authenticated && user && (
            <div
              className="mb-4 flex items-center gap-3 rounded-md border bg-muted/40 p-3"
              data-test-id="already-signed-in"
            >
              <UserAvatar
                email={user.email}
                username={user.initials ? undefined : user.name}
                initials={user.initials}
                avatarUrl={user.avatar}
                backgroundColor={user.avatarBgColor}
                size="sm"
                className="shrink-0"
              />
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-muted-foreground">
                  You&apos;re signed in as{" "}
                  <span className="font-medium text-foreground">
                    {user.name || user.email}
                  </span>
                  .{" "}
                  <Link href="/profile" className="underline underline-offset-4">
                    View your profile
                  </Link>
                  , or sign in below to switch to another account.
                </p>
              </div>
            </div>
          )}

          {loginOptions && suggestedMethod && (
            <div
              className="mb-4 flex items-center gap-3 rounded-md border bg-muted/40 p-3"
              data-test-id="login-suggestion"
            >
              {loginOptions.profile &&
                (loginOptions.profile.avatarUrl ? (

                  <img
                    src={loginOptions.profile.avatarUrl}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-full object-cover"
                  />
                ) : loginOptions.profile.initials ? (
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: loginOptions.profile.avatarBgColor || "#6b7280" }}
                  >
                    {loginOptions.profile.initials}
                  </span>
                ) : null)}
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-muted-foreground">
                  Welcome back — last time you signed in with{" "}
                  <span className="font-medium text-foreground">
                    {methodLabel(suggestedMethod)}
                  </span>
                  .
                </p>
              </div>
            </div>
          )}

          {error && (
            <div
              className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive"
              data-test-id="error-message"
            >
              {error}
            </div>
          )}

          {unverifiedEmail && (
            <div
              className="mb-4 rounded-md bg-muted p-3 text-sm"
              data-test-id="unverified-account"
            >
              <p className="mb-2">
                Your email address hasn&apos;t been verified yet. Check your inbox for the
                verification link we sent to <strong>{unverifiedEmail}</strong>.
              </p>
              {resendState === "sent" ? (
                <p className="text-muted-foreground" data-test-id="resend-sent">
                  Sent. If it doesn&apos;t arrive, check your spam folder.
                </p>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResendVerification}
                  disabled={resendState === "sending"}
                  data-test-id="resend-verification"
                >
                  {resendState === "sending" ? "Sending…" : "Resend verification email"}
                </Button>
              )}
              {resendState === "failed" && (
                <p className="mt-2 text-destructive">
                  Couldn&apos;t send right now. Please try again in a moment.
                </p>
              )}
            </div>
          )}

          {formIsPrimary ? (
            // No recent method, or password is the recent method: the form leads.
            <>
              {formBlock}
              {(alternateMethods.length > 0 || oauthBlock) && (
                <Accordion type="single" collapsible className="mt-2">
                  <AccordionItem value="other" className="border-b-0">
                    <AccordionTrigger
                      className="text-sm text-muted-foreground hover:no-underline"
                      data-test-id="other-options-trigger"
                    >
                      Other sign in options
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-1">
                        {alternateMethodsBlock}
                        {oauthBlock}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </>
          ) : (
            // A passwordless method is the recent one: lead with it, collapse the rest.
            <>
              <Button
                type="button"
                className="w-full"
                disabled={isOAuthLoading || isPasskeyLoading}
                onClick={() => runMethod(primaryMethod!)}
                data-test-id="primary-method-action"
              >
                {primaryMethod === "passkey" && isPasskeyLoading
                  ? "Authenticating..."
                  : `Sign in with ${methodActionLabel(primaryMethod!)}`}
              </Button>

              <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="other" className="border-b-0">
                  <AccordionTrigger
                    className="text-sm text-muted-foreground hover:no-underline"
                    data-test-id="other-options-trigger"
                  >
                    Other sign in options
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-1">{otherOptionsBlock}</div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </>
          )}
        </CardContent>
        <CardFooter>
          <div className="text-sm text-muted-foreground flex flex-row justify-between w-100">
            <Link
              href={appendReturnTo("/login/options")}
              className="underline hover:text-primary justify-self-end-safe"
              data-test-id="back-to-login-link"
            >
              Other sign in methods
            </Link>
            <Link
              href="/register/options"
              className="underline hover:text-primary justify-self-end-safe"
              data-test-id="back-to-login-link"
            >
              Create an account
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
